import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./delivery-ordering.css";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@finitless/shared/components";
import { Icon } from "../../ui";
import DeliveryGroupCard from "../../ui/Delivery/DeliveryGroupCard";
import Pagination from "../../ui/Data/Pagination";
import UsersLoading from "../../ui/Users/UsersLoading";
import UsersError from "../../ui/Users/UsersError";
import {
  useDeliveryGroups,
  useDeliveryGroupStats,
  useToggleDeliveryGroupStatus,
} from "../../../hooks";

const ITEMS_PER_PAGE = 10;
const MAX_VISIBLE_PAGES = 5;

const DeliveryGroupsPage = () => {
  const { t } = useTranslation("delivery");
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Get delivery groups data
  const {
    data: groupsData,
    isLoading,
    error,
  } = useDeliveryGroups(searchTerm, currentPage);

  // Get stats
  const { data: statsData } = useDeliveryGroupStats(searchTerm);

  // Toggle status mutation
  const toggleStatusMutation = useToggleDeliveryGroupStatus();

  const groups = groupsData?.deliveryGroups || [];
  const pagination = groupsData?.pagination || {};
  const total = statsData?.total || pagination?.total || 0;

  // Calculate totalPages
  const calculatedTotalPages = total ? Math.ceil(total / ITEMS_PER_PAGE) : 1;

  const totalPages = pagination?.totalPages || calculatedTotalPages;

  const handleSearchChange = (term) => {
    setSearchInput(term);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDetails = (group) => {
    navigate(`/delivery/groups/${group.id}`, { state: { group } });
  };

  const handleToggleStatus = (groupId, enabled) => {
    toggleStatusMutation.mutate({ groupId, enabled });
  };

  return (
    <div className="ordering-delivery-surface space-y-6">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <PageHeader
          title={t("groups.header.title")}
          subtitle={t("groups.header.subtitle")}
        />
        <div className="flex w-full sm:w-auto">
          <Button
            emphasis="solid"
            intent="brand"
            size="touch"
            onClick={() => navigate("/delivery/groups/create")}
            className="flex w-full items-center justify-center space-x-2 px-4 py-2 transition-colors sm:w-auto"
          >
            <Icon name="Plus" className="h-4 w-4" />
            <span>{t("groups.actions.create")}</span>
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="border-border bg-background mb-6 rounded-lg border p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <div className="ordering-delivery-search relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon name="Search" className="text-muted-foreground h-5 w-5" />
              </div>
              <input
                type="text"
                placeholder={t("groups.searchPlaceholder")}
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="border-border bg-card focus:ring-ring h-10 w-full rounded-lg border px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:outline-none"
              />
              {searchInput && (
                <Button
                  variant="ghost"
                  onClick={handleClearSearch}
                  aria-label={t("agentTranslations:actions.clearSearch")}
                  className="bg-muted hover:bg-muted absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full transition-colors [&_svg]:size-3"
                  type="button"
                >
                  <svg
                    aria-hidden="true"
                    className="text-foreground h-3 w-3 hover:text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <UsersLoading />
      ) : error ? (
        <UsersError error={error} />
      ) : groups.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {groups.map((group) => (
              <DeliveryGroupCard
                key={group.id}
                group={group}
                onViewDetails={handleViewDetails}
                onToggleStatus={handleToggleStatus}
                isToggling={toggleStatusMutation.isPending}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-8">
            <div className="border-border bg-background rounded-lg border p-4">
              <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                {/* Pagination Info */}
                <div className="text-muted-foreground text-sm">
                  {t("groups.pagination.showingPrefix")}{" "}
                  <span className="text-foreground font-medium">
                    {(currentPage - 1) * ITEMS_PER_PAGE + 1}
                  </span>{" "}
                  {t("groups.pagination.to")}{" "}
                  <span className="text-foreground font-medium">
                    {Math.min(currentPage * ITEMS_PER_PAGE, total)}
                  </span>{" "}
                  {t("groups.pagination.of")}{" "}
                  <span className="text-foreground font-medium">{total}</span>{" "}
                  {t("groups.pagination.results")}
                </div>

                {/* Pagination Controls */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  maxVisiblePages={MAX_VISIBLE_PAGES}
                  disabled={isLoading}
                  className="mt-4 sm:mt-0"
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="py-12 text-center">
          <Icon
            name="Users"
            className="text-muted-foreground mx-auto mb-4 h-12 w-12"
          />
          <h3 className="text-foreground mt-2 text-sm font-medium">
            {t("groups.empty.title")}
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {searchTerm
              ? t("groups.empty.searchHint")
              : t("groups.empty.defaultHint")}
          </p>
        </div>
      )}
    </div>
  );
};

export default DeliveryGroupsPage;
